<template>
  <div class="summary-panel" v-if="summaryItems.length > 0 || parseFloat(String(total ?? 0)) > 0" data-testid="payroll-cash-recebimentos-summary">
    <h4 class="summary-title">
      <i class="pi pi-chart-bar"></i>
      {{ t('Resumo de Adiantamentos') }}
    </h4>

    <!-- Summary by user -->
    <div v-if="summaryItems.length > 0" class="user-summary-list">
      <div
        v-for="(item, index) in summaryItems"
        :key="item.user_id || index"
        class="user-summary-item"
      >
        <div class="user-info">
          <i class="pi pi-user"></i>
          <span class="user-name">{{ getUserName(item.user_id) }}</span>
        </div>
        <div class="user-stats">
          <Badge :value="item.advance_count || 0" severity="info" class="count-badge" />
          <span class="total-amount">{{ n(item.total_advances || 0, 'currency') }}</span>
        </div>
      </div>
    </div>

    <!-- Total row -->
    <div class="total-row">
      <span class="total-label">{{ t('Total Adiantamentos') }}:</span>
      <span class="total-value">{{ n(parseFloat(String(total ?? 0)) || 0, 'currency') }}</span>
    </div>

    <!-- Info message -->
    <div class="info-note">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('Estes valores serão descontados do salário final de cada colaborador na confirmação do período.') }}</span>
    </div>
  </div>

  <div v-else class="summary-panel empty">
    <div class="empty-summary">
      <i class="pi pi-wallet"></i>
      <span>{{ t('Nenhum adiantamento selecionado') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { permissionsService } from '@/services/permissionsService';

type Id = number | string;

interface SummaryRow {
  user_id?: Id;
  advance_count?: number | string;
  total_advances?: number | string;
}

interface UserInfo {
  name?: string;
  user_name?: string;
  [key: string]: unknown;
}

interface Props {
  summary?: SummaryRow[];
  total?: number | string;
  usersMap?: Record<string, UserInfo>;
}

const props = withDefaults(defineProps<Props>(), {
  summary: () => [],
  total: 0,
  usersMap: () => ({}),
});

const { t, n } = useI18n();

// Convert summary to plain array to avoid Vue reactive proxy issues.
// Preserves original numeric coercion: parseInt on advance_count,
// parseFloat on total_advances. Using String(... ?? 0) to keep
// runtime behavior identical to original `parseFloat(value)` (which
// JS would coerce internally) while satisfying TS strict typing.
const summaryItems = computed<{ user_id?: Id; advance_count: number; total_advances: number }[]>(() => {
  if (!Array.isArray(props.summary)) return [];
  return props.summary.map((item) => ({
    user_id: item.user_id,
    advance_count: parseInt(String(item.advance_count ?? 0), 10) || 0,
    total_advances: parseFloat(String(item.total_advances ?? 0)) || 0,
  }));
});

const getUserName = (userId: Id | undefined): string => {
  if (userId === undefined || userId === null) return '';
  const key = String(userId);
  return (
    props.usersMap?.[key]?.name ||
    props.usersMap?.[key]?.user_name ||
    permissionsService.getUserNameBySub(key) ||
    String(userId)
  );
};
</script>

<style scoped>
.summary-panel {
  background: var(--surface-50);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  border: 2px solid var(--surface-200);
}

.summary-panel.empty {
  border-style: dashed;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: var(--text-color);
}

.summary-title i {
  color: var(--orange-500);
}

.user-summary-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.user-summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid var(--surface-200);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-info i {
  color: var(--text-color-secondary);
}

.user-name {
  font-weight: 500;
}

.user-stats {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.count-badge {
  font-size: 0.75rem;
}

.total-amount {
  font-weight: 600;
  color: var(--orange-600);
  font-size: 1.1rem;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--orange-50);
  border-radius: 6px;
  border-left: 4px solid var(--orange-500);
  margin-bottom: 1rem;
}

.total-label {
  font-weight: 600;
  font-size: 1rem;
  color: var(--orange-900);
}

.total-value {
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--orange-600);
}

.info-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--blue-50);
  border-radius: 6px;
  font-size: 0.9rem;
  color: var(--blue-800);
}

.info-note i {
  color: var(--blue-500);
  margin-top: 2px;
}

.empty-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  color: var(--text-color-secondary);
}

.empty-summary i {
  font-size: 1.5rem;
}
</style>
